import {
	Dialog,
	DialogTitle,
	DialogContent,
	IconButton,
	Typography,
	Stack,
	CircularProgress,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	TableFooter,
	TextField,
	InputAdornment,
	IconButton as MIconButton,
	Box,
	Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useEffect, useState } from "react";
import { ComissionServices } from "../../../../shared/services/api/comission/ComissionServices";
import { format } from "date-fns";
import { useDebounce } from "../../../../shared/hooks";
import {
	globalFormatCurrencyBRL,
	globalMaskDocument,
	globalTranslateStatusComission,
	globalTranslateTypeComission,
	translateProposal,
} from "../../../../shared/utils";
import { globalcolorBackGroundComission, globalcolorTextComision } from "../../../../shared/utils/global-utils-comission";

interface Props {
	open: boolean;
	onClose: () => void;
	comissionId: string;
	dataIni: string;
	dataFim: string;
}

interface Item {
	id: string;
	clientId: string;
	clientName: string;
	clientDocument: string;
	proposalCertificateNumber: string | null;
	proposalName: string | null;
	type: string;
	installmentValue: string;
	description: string;
	value: string;
	dueDate: string;
	installmentNumber?: number;
	proposalRentMonthsCount?: number;
	proposalDocument?: string;
	status: string;
}

export const ModalDetailComission = ({ open, onClose, comissionId, dataIni, dataFim }: Props) => {
	const [items, setItems] = useState<Item[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [searchText, setSearchText] = useState("");
	const [debouncedSearchText, setDebouncedSearchText] = useState("");
	const { debounce } = useDebounce();

	useEffect(() => {
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const result = await ComissionServices.getComissionValuesDetail(comissionId, dataIni, dataFim);
				setItems(Array.isArray(result.results) ? result.results : []);
			} catch (error) {
				console.error("Erro ao buscar detalhes:", error);
				setItems([]);
			} finally {
				setIsLoading(false);
			}
		};

		if (open && comissionId) fetchData();
	}, [open, comissionId]);

	useEffect(() => {
		debounce(() => setDebouncedSearchText(searchText));
	}, [searchText]);

	const normalize = (text: string) => text.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

	const filteredItems = items.filter((item) => normalize(`${item.proposalName ?? ""} ${item.proposalDocument}`).includes(normalize(debouncedSearchText)));

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
			<DialogTitle
				sx={{
					color: "primary.main",
					fontWeight: 400,
					display: "flex",
					justifyContent: "space-between",
				}}
			>
				<Typography variant="h5">{`Detalhes do Faturamento - ${items[0]?.clientName?.trim() || ""}`}</Typography>
				<IconButton onClick={onClose}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers>
				{isLoading ? (
					<Stack alignItems="center" py={4}>
						<CircularProgress />
						<Typography variant="body2" mt={2}>
							Carregando...
						</Typography>
					</Stack>
				) : (
					<>
						<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
							<TextField
								placeholder="Pesquisar por nome ou documento"
								variant="outlined"
								size="small"
								fullWidth
								value={searchText}
								onChange={(e) => setSearchText(e.target.value)}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon />
										</InputAdornment>
									),
									endAdornment: searchText && (
										<InputAdornment position="end">
											<MIconButton size="small" onClick={() => setSearchText("")}>
												<ClearIcon fontSize="small" />
											</MIconButton>
										</InputAdornment>
									),
								}}
								sx={{ maxWidth: 400 }}
							/>

							<Typography variant="body2" sx={{ ml: 2 }}>
								{filteredItems.length} registro(s) encontrado(s)
							</Typography>
						</Stack>
						<Box sx={{ maxHeight: 420, overflowY: "auto" }}>
							<Table size="small">
								<TableHead>
									<TableRow sx={{ backgroundColor: "grey.100" }}>
										{["CERTIFICADO", "NOME", "TIPO", "VALOR", "VENCIMENTO", "OBSERVAÇÃO", "STATUS"].map((col, i) => (
											<TableCell key={i}>
												<Typography variant="button">{col}</Typography>
											</TableCell>
										))}
									</TableRow>
								</TableHead>

								<TableBody>
									{filteredItems.map((item) => (
										<TableRow key={item.id}>
											<TableCell>{item.proposalCertificateNumber ?? "-"}</TableCell>
											<TableCell>
												<Typography fontSize="0.875rem">{item.proposalName!!}</Typography>
												<Typography fontSize="0.875rem" fontWeight="bold">
													{globalMaskDocument(item.proposalDocument??'')}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography fontSize="0.875rem">{globalTranslateTypeComission(item.type)}</Typography>
												{item.type === "prize" && item.installmentNumber && item.proposalRentMonthsCount && (
													<Typography variant="caption" color="textSecondary">
														{item.installmentNumber}/{item.proposalRentMonthsCount}
													</Typography>
												)}
											</TableCell>
											<TableCell>
												<Typography fontWeight="bold">{globalFormatCurrencyBRL(item.value)}</Typography>
											</TableCell>
											<TableCell>{format(new Date(item.dueDate), "dd/MM/yyyy")}</TableCell>
											<TableCell>{item.description}</TableCell>

											<TableCell>
												<Chip
													label={translateProposal(globalTranslateStatusComission(item.status))}
													size="small"
													variant="filled"
													sx={{
														backgroundColor: globalcolorBackGroundComission(item.status),
														color: globalcolorTextComision(item.status),
														fontWeight: "normal",
													}}
												/>
											</TableCell>
										</TableRow>
									))}
								</TableBody>

								<TableFooter>
									<TableRow>
										<TableCell colSpan={7}>
											<Stack spacing={1} alignItems="flex-end" py={3}>
												<Typography variant="subtitle2" fontWeight={500} sx={{ mb: 1 }}>
													Resumo por Tipo de Comissão
												</Typography>

												<Table size="small" sx={{ width: 400, border: "1px solid #e0e0e0" }}>
													<TableHead>
														<TableRow sx={{ backgroundColor: "grey.100" }}>
															<TableCell>
																<Typography variant="caption" fontWeight={600}>
																	Tipo
																</Typography>
															</TableCell>
															<TableCell align="center">
																<Typography variant="caption" fontWeight={600}>
																	Quantidade
																</Typography>
															</TableCell>
															<TableCell align="right">
																<Typography variant="caption" fontWeight={600}>
																	Valor Total
																</Typography>
															</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{["prize", "bonus", "discount"].map((type) => {
															const label = type === "prize" ? "Premiação" : type === "bonus" ? "Bonificação" : "Estorno";

															// ✅ Somar apenas status === "preview"
															const entries = filteredItems.filter((i) => i.type === type && i.status === "preview");

															const count = entries.length;
															const total = entries.reduce((acc, i) => acc + Number(i.value), 0);

															return (
																<TableRow key={type}>
																	<TableCell>
																		<Typography variant="body2">{label}</Typography>
																	</TableCell>
																	<TableCell align="center">
																		<Typography variant="body2">{count}</Typography>
																	</TableCell>
																	<TableCell align="right">
																		<Typography variant="body2">{globalFormatCurrencyBRL(total.toString())}</Typography>
																	</TableCell>
																</TableRow>
															);
														})}
													</TableBody>
													<TableFooter>
														<TableRow sx={{ backgroundColor: "#f4fff4" }}>
															<TableCell>
																<Typography fontWeight={600}>Total</Typography>
															</TableCell>
															<TableCell />
															<TableCell align="right">
																<Typography fontWeight={700} color="primary">
																	{globalFormatCurrencyBRL(
																		filteredItems
																			.filter((i) => i.status === "preview")
																			.reduce((acc, i) => acc + Number(i.value), 0)
																			.toString()
																	)}
																</Typography>
															</TableCell>
														</TableRow>
													</TableFooter>
												</Table>
											</Stack>
										</TableCell>
									</TableRow>
								</TableFooter>
							</Table>
						</Box>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};
